import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from '@/components/ui/toaster'
import { useAuth } from '@/hooks/useAuth'
import { useWebSocket } from '@/hooks/useWebSocket'
import { ImpersonationProvider } from '@/hooks/useImpersonation'
import { BrandProvider } from '@/hooks/useBrand'
import { FeatureGateProvider } from '@/hooks/useFeatureGate'
import { UpgradeModal } from '@/components/UpgradeModal'
import { FeatureGate } from '@/components/FeatureGate'
import { SessionExpiryWarning } from '@/components/SessionExpiryWarning'
import { ErrorBoundary } from '@/components/ErrorBoundary'

// Pages
import LoginPage from '@/pages/LoginPage'
import ResetPasswordPage from '@/pages/ResetPasswordPage'
import VerifyEmailPage from '@/pages/VerifyEmailPage'
import AcceptInvitePage from '@/pages/AcceptInvitePage'
import RedeemPage from '@/pages/RedeemPage'
import DashboardPage from '@/pages/DashboardPage'
import ChatHubPage from '@/pages/ChatHubPage'
import LeadsPage from '@/pages/LeadsPage'
import LeadDetailPage from '@/pages/LeadDetailPage'
import AnalyticsPage from '@/pages/AnalyticsPage'
import SettingsPage from '@/pages/SettingsPage'
import WidgetPage from '@/pages/WidgetPage'
import CallsPage from '@/pages/CallsPage'
import SmsPage from '@/pages/SmsPage'
import BookingsPage from '@/pages/BookingsPage'
import MessagesPage from '@/pages/MessagesPage'
import TeamPage from '@/pages/TeamPage'
import IntegrationsPage from '@/pages/IntegrationsPage'
import FormConnectionsPage from '@/pages/FormConnectionsPage'
import AIAgentsPage from '@/pages/AIAgentsPage'
import AgentInsightsPage from '@/pages/AgentInsightsPage'
import FollowUpQueuePage from '@/pages/FollowUpQueuePage'
import AffiliateApplyPage from '@/pages/AffiliateApplyPage'
import AffiliateRedirectPage from '@/pages/AffiliateRedirectPage'
import AffiliateDashboardPage from '@/pages/AffiliateDashboardPage'
import AffiliateEmailPage from '@/pages/AffiliateEmailPage'
import AffiliateWalkthroughPage from '@/pages/AffiliateWalkthroughPage'

// Legal
import TermsPage from '@/pages/TermsPage'
import PrivacyPage from '@/pages/PrivacyPage'

// Public
import DemoPage from '@/pages/DemoPage'
import PricingPage from '@/pages/PricingPage'
import BillingPage from '@/pages/BillingPage'
import BillingPlansPage from '@/pages/BillingPlansPage'
import SandboxPreviewPage from '@/pages/SandboxPreviewPage'

import TeamProfilePage from '@/pages/TeamProfilePage'

// Onboarding
import { OnboardingPage } from '@/pages/onboarding'

// Platform Admin
import { PlatformDashboard, OrganizationsPage as AdminOrganizationsPage, OrganizationDetailPage, VoiceManagementPage, DemoLeadsPage, DemoDncPage, UsersAdminPage, CustomLlmProvidersPage, AccessCodesPage, AffiliatesPage, DealBuilderPage, FranchisesPage } from '@/pages/admin'
import DealContractPage from '@/pages/DealContractPage'

// Franchise (reseller) console
import FranchiseBrandingPage from '@/pages/franchise/FranchiseBrandingPage'
import FranchiseClientsPage from '@/pages/franchise/FranchiseClientsPage'
import { useFranchise } from '@/hooks/useFranchise'

// Layout
import DashboardLayout from '@/components/layout/DashboardLayout'
import LoadingSpinner from '@/components/ui/LoadingSpinner'

function HomeRoute() {
  return <DashboardPage />
}

// Route guard for platform admin pages — redirects non-admins to home.
// localStorage is the source of truth (NOT React state from useAuth) so that
// Safari bfcache restores can't let a stale admin session leak through. If
// the JWT in storage doesn't claim isPlatformAdmin, the guard denies — even
// if the in-memory React state thinks the user is an admin from a previous
// life of the page.
function PlatformAdminGuard({ children }: { children: React.ReactNode }) {
  let isPlatformAdmin = false
  try {
    const token = localStorage.getItem('app_auth_token')
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]))
      if (payload.exp && payload.exp * 1000 < Date.now()) {
        localStorage.removeItem('app_auth_token')
        localStorage.removeItem('app_agent_data')
      } else {
        isPlatformAdmin = !!payload.isPlatformAdmin
      }
    }
  } catch { /* ignore decode errors — treat as not-admin */ }

  // Fallback: stored agent_data (kept for cases where the JWT predates the
  // is_platform_admin claim — defensive belt + braces).
  if (!isPlatformAdmin) {
    try {
      const stored = localStorage.getItem('app_agent_data')
      if (stored) {
        isPlatformAdmin = !!JSON.parse(stored).isPlatformAdmin
      }
    } catch { /* ignore parse errors */ }
  }

  if (!isPlatformAdmin) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

// Route guard for the franchise (reseller) console. Server-resolved: the
// client never decides franchise-admin status itself.
function FranchiseAdminGuard({ children }: { children: React.ReactNode }) {
  const { isFranchiseAdmin, loading } = useFranchise()
  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <LoadingSpinner size="lg" />
      </div>
    )
  }
  if (!isFranchiseAdmin) {
    return <Navigate to="/" replace />
  }
  return <>{children}</>
}

function AppContent() {
  const {
    isAuthenticated,
    isLoading,
    agent,
    onboardingComplete,
    sessionExpiryWarning,
    sessionExpiresAt,
    extendSession,
    dismissSessionWarning,
    logout
  } = useAuth()
  const { connect, disconnect } = useWebSocket()

  // Connect to WebSocket when authenticated
  React.useEffect(() => {
    if (isAuthenticated && agent) {
      connect(agent.id)
    } else {
      disconnect()
    }
  }, [isAuthenticated, agent, connect, disconnect])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route path="/redeem" element={<RedeemPage />} />
          <Route path="/onboarding/*" element={<OnboardingPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/demo" element={<DemoPage />} />
          <Route path="/try" element={<DemoPage />} />
          <Route path="/preview/:slug" element={<SandboxPreviewPage />} />
          <Route path="/deal/:token" element={<DealContractPage />} />
          <Route path="/affiliate/apply" element={<AffiliateApplyPage />} />
          <Route path="/affiliate/:code" element={<AffiliateRedirectPage />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        <Toaster />
      </>
    )
  }

  // Authenticated but onboarding not complete - redirect to onboarding
  // Platform admins bypass onboarding (they manage all orgs, don't belong to one)
  let isPlatformAdmin = (agent as any)?.isPlatformAdmin
  if (!isPlatformAdmin) {
    try {
      const stored = localStorage.getItem('app_agent_data')
      if (stored) isPlatformAdmin = JSON.parse(stored).isPlatformAdmin
    } catch { /* ignore */ }
  }
  if (!onboardingComplete && !isPlatformAdmin) {
    return (
      <>
        <Routes>
          <Route path="/onboarding/*" element={<OnboardingPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/demo" element={<DemoPage />} />
          <Route path="/try" element={<DemoPage />} />
          <Route path="/preview/:slug" element={<SandboxPreviewPage />} />
          <Route path="/deal/:token" element={<DealContractPage />} />
          <Route path="/affiliate/apply" element={<AffiliateApplyPage />} />
          <Route path="/affiliate/:code" element={<AffiliateRedirectPage />} />
          <Route path="*" element={<Navigate to="/onboarding" replace />} />
        </Routes>
        <Toaster />
      </>
    )
  }

  return (
    <>
      <Routes>
        <Route path="/" element={<DashboardLayout />}>
          <Route index element={<HomeRoute />} />
          <Route path="chats" element={<ChatHubPage />} />
          <Route path="conversations" element={<ChatHubPage />} />
          <Route path="leads" element={<LeadsPage />} />
          <Route path="leads/:id" element={<LeadDetailPage />} />
          <Route path="calls" element={<FeatureGate feature="ai_inbound_agent"><CallsPage /></FeatureGate>} />
          <Route path="follow-up-queue" element={<FeatureGate feature="auto_callback"><FollowUpQueuePage /></FeatureGate>} />
          <Route path="sms" element={<SmsPage />} />
          <Route path="bookings" element={<BookingsPage />} />
          <Route path="messages" element={<MessagesPage />} />
          <Route path="agents" element={<FeatureGate feature="ai_inbound_agent"><AIAgentsPage /></FeatureGate>} />
          <Route path="agents/:id/insights" element={<FeatureGate feature="ai_inbound_agent"><AgentInsightsPage /></FeatureGate>} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="users" element={<Navigate to="/team" replace />} />
          <Route path="team" element={<TeamPage />} />
          <Route path="team/profile/:userId" element={<TeamProfilePage />} />
          <Route path="integrations" element={<FeatureGate feature="crm_integrations"><IntegrationsPage /></FeatureGate>} />
          <Route path="form-connections" element={<FormConnectionsPage />} />
          <Route path="lead-webhooks" element={<FormConnectionsPage />} /> {/* Redirect old URL */}
          <Route path="widget" element={<WidgetPage />} />
          <Route path="affiliate" element={<AffiliateDashboardPage />} />
          <Route path="affiliate/email" element={<AffiliateEmailPage />} />
          <Route path="affiliate/walkthrough" element={<AffiliateWalkthroughPage />} />
          {/* Deal Builder for affiliates — access enforced server-side; the
              page itself shows an access-denied state when not enabled. */}
          <Route path="affiliate/deals" element={<DealBuilderPage />} />
          {/* Franchise (reseller) console — guarded by FranchiseAdminGuard */}
          <Route path="franchise/clients" element={<FranchiseAdminGuard><FranchiseClientsPage /></FranchiseAdminGuard>} />
          <Route path="franchise/branding" element={<FranchiseAdminGuard><FranchiseBrandingPage /></FranchiseAdminGuard>} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="settings/billing" element={<BillingPage />} />
          <Route path="settings/billing/plans" element={<BillingPlansPage />} />
          <Route path="billing" element={<Navigate to="/settings/billing" replace />} />
          <Route path="billing/plans" element={<Navigate to="/settings/billing/plans" replace />} />
          {/* Platform Admin Routes — guarded by PlatformAdminGuard */}
          <Route path="admin" element={<PlatformAdminGuard><PlatformDashboard /></PlatformAdminGuard>} />
          <Route path="admin/franchises" element={<PlatformAdminGuard><FranchisesPage /></PlatformAdminGuard>} />
          <Route path="admin/organizations" element={<PlatformAdminGuard><AdminOrganizationsPage /></PlatformAdminGuard>} />
          <Route path="admin/organizations/:orgId" element={<PlatformAdminGuard><OrganizationDetailPage /></PlatformAdminGuard>} />
          <Route path="admin/users" element={<PlatformAdminGuard><UsersAdminPage /></PlatformAdminGuard>} />
          <Route path="admin/voice" element={<PlatformAdminGuard><VoiceManagementPage /></PlatformAdminGuard>} />
          <Route path="admin/custom-llm-providers" element={<PlatformAdminGuard><CustomLlmProvidersPage /></PlatformAdminGuard>} />
          <Route path="admin/access-codes" element={<PlatformAdminGuard><AccessCodesPage /></PlatformAdminGuard>} />
          <Route path="admin/demo-leads" element={<PlatformAdminGuard><DemoLeadsPage /></PlatformAdminGuard>} />
          <Route path="admin/demo-dnc" element={<PlatformAdminGuard><DemoDncPage /></PlatformAdminGuard>} />
          <Route path="admin/affiliates" element={<PlatformAdminGuard><AffiliatesPage /></PlatformAdminGuard>} />
          <Route path="admin/deals" element={<PlatformAdminGuard><DealBuilderPage /></PlatformAdminGuard>} />
        </Route>
        <Route path="/demo" element={<DemoPage />} />
        <Route path="/try" element={<DemoPage />} />
        <Route path="/preview/:slug" element={<SandboxPreviewPage />} />
        <Route path="/deal/:token" element={<DealContractPage />} />
        <Route path="/affiliate/apply" element={<AffiliateApplyPage />} />
        <Route path="/affiliate/:code" element={<AffiliateRedirectPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        {/* Reset-password must be reachable while authenticated too —
            otherwise clicking a magic-link from an email while already
            signed in (e.g. an admin who just approved an affiliate as
            their own email) bounces straight to / and the user never
            gets to set the new password. */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster />
      <SessionExpiryWarning
        isVisible={sessionExpiryWarning}
        expiresAt={sessionExpiresAt}
        onExtend={extendSession}
        onDismiss={dismissSessionWarning}
        onLogout={logout}
      />
    </>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <BrandProvider>
        <ImpersonationProvider>
          <FeatureGateProvider>
            <AppContent />
            <UpgradeModal />
          </FeatureGateProvider>
        </ImpersonationProvider>
      </BrandProvider>
    </ErrorBoundary>
  )
}

export default App
